import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { uploadFile, openFileInNewTab, downloadFileAttachment } from '../../services/fileApi';

/**
 * Supporting documents of a procurement document (purchase request / order, GRN, supplier return,
 * debit note, supplier payment): invoices, delivery notes, specifications, contracts, payment proofs…
 *
 * With a docId, files are uploaded and attached straight away. Without one (a new document still being
 * filled in) the chosen files wait in the list; the form calls ref.uploadPending(newId) after saving.
 *
 * docType: purchase-requests | purchase-orders | grn | supplier-return-notes | debit-notes | supplier-payments
 */
export const DOCUMENT_CATEGORIES = ['Invoice', 'Delivery Note', 'Quotation', 'Specification', 'Contract / Agreement', 'Approval', 'Payment Proof', 'Photo', 'Other'];
const MAX_MB = 25;

const fmtSize = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};
const fmtDate = (d) => (d ? new Date(d).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '');

// Upload to the file service, then attach to the procurement document
const uploadAndAttach = async (docType, docId, file, category) => {
  const res = await uploadFile(file, { category: 'Other', title: file.name, description: `${docType} ${category}` });
  const d = res.data?.data || {};
  if (!d.fileId) throw new Error(`${file.name} was not stored`);
  const att = await api.post(`/inventory/procurement-documents/${docType}/${docId}`, {
    fileId: d.fileId,
    fileName: d.originalName || file.name,
    fileType: d.mimeType || file.type || '',
    fileSize: d.fileSize || file.size || 0,
    category
  });
  return att.data?.data;
};

const ProcurementDocuments = forwardRef(({ docType, docId = null, canEdit = true, title = 'Documents', className = '' }, ref) => {
  const [documents, setDocuments] = useState([]);
  const [pending, setPending] = useState([]); // [{ key, file, category }]
  const pendingRef = useRef([]);
  const [category, setCategory] = useState('Invoice');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const setPendingBoth = (next) => { pendingRef.current = next; setPending(next); };

  const load = useCallback(async () => {
    if (!docId) { setDocuments([]); return; }
    setLoading(true);
    try {
      const res = await api.get(`/inventory/procurement-documents/${docType}/${docId}`);
      setDocuments(res.data?.data || []);
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  }, [docType, docId]);

  useEffect(() => { load(); }, [load]);

  // A new document's files are uploaded once the form has saved it (returns { uploaded, failed })
  useImperativeHandle(ref, () => ({
    hasPending: () => pendingRef.current.length > 0,
    uploadPending: async (newId) => {
      const list = pendingRef.current;
      if (!newId || !list.length) return { uploaded: 0, failed: [] };
      const failed = [];
      let uploaded = 0;
      for (const p of list) {
        try { await uploadAndAttach(docType, newId, p.file, p.category); uploaded += 1; } catch (err) { failed.push(p.file.name); }
      }
      pendingRef.current = [];
      if (failed.length) toast.error(`Saved, but ${failed.length} document(s) did not upload: ${failed.join(', ')} — add them from the document's view`);
      else if (uploaded) toast.success(`${uploaded} document(s) attached`);
      return { uploaded, failed };
    }
  }), [docType]);

  const pickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const tooBig = files.filter(f => f.size > MAX_MB * 1024 * 1024);
    if (tooBig.length) toast.error(`Larger than ${MAX_MB} MB: ${tooBig.map(f => f.name).join(', ')}`);
    const ok = files.filter(f => f.size <= MAX_MB * 1024 * 1024);
    if (!ok.length) return;

    if (!docId) {
      setPendingBoth([...pendingRef.current, ...ok.map(file => ({ key: `${file.name}-${file.size}-${Math.random()}`, file, category }))]);
      return;
    }
    setBusy(true);
    let done = 0;
    for (const file of ok) {
      try { await uploadAndAttach(docType, docId, file, category); done += 1; } catch (err) {
        toast.error(err.response?.data?.message || `${file.name}: ${err.message || 'upload failed'}`);
      }
    }
    setBusy(false);
    if (done) toast.success(`${done} document(s) attached`);
    load();
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove ${d.fileName}?`)) return;
    try {
      const res = await api.delete(`/inventory/procurement-documents/${docType}/${docId}/${d._id}`);
      toast.success(res.data?.message || 'Document removed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove the document');
    }
  };

  const view = async (d) => {
    try {
      await openFileInNewTab(d.fileId, { mimeType: d.fileType || 'application/octet-stream' });
    } catch (err) {
      toast.error('Could not open the file');
    }
  };
  const download = async (d) => {
    try {
      await downloadFileAttachment(d.fileId, d.fileName, d.fileType || 'application/octet-stream');
    } catch (err) {
      toast.error('Could not download the file');
    }
  };

  const count = documents.length + pending.length;

  return (
    <div className={`border border-gray-200 rounded-lg overflow-hidden ${className}`} data-testid={`documents-${docType}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 px-3 py-2 border-b border-gray-200">
        <span className="text-sm font-semibold text-gray-700">
          {title}{count ? ` (${count})` : ''}
        </span>
        {canEdit && (
          <div className="flex items-center gap-2">
            <select value={category} onChange={(e) => setCategory(e.target.value)} disabled={busy}
              className="px-2 py-1 text-xs border border-gray-300 rounded bg-white" aria-label="Document category">
              {DOCUMENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded disabled:opacity-50">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              {busy ? 'Uploading…' : 'Attach files'}
            </button>
            <input ref={inputRef} type="file" multiple className="hidden" onChange={pickFiles} data-testid="documents-input"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip" />
          </div>
        )}
      </div>

      <div className="divide-y divide-gray-100">
        {loading && <p className="px-3 py-3 text-xs text-gray-500">Loading documents…</p>}
        {!loading && count === 0 && (
          <p className="px-3 py-3 text-xs text-gray-500">
            No documents attached{canEdit ? ' — attach invoices, delivery notes, specifications, contracts or payment proofs (PDF, images, Office files; up to 25 MB each)' : ''}.
          </p>
        )}
        {pending.map(p => (
          <div key={p.key} className="flex items-center gap-3 px-3 py-2 text-sm">
            <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-700">ON SAVE</span>
            <span className="flex-1 truncate text-gray-800">{p.file.name}</span>
            <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600">{p.category}</span>
            <span className="text-xs text-gray-400 w-16 text-right">{fmtSize(p.file.size)}</span>
            <button type="button" title="Remove" onClick={() => setPendingBoth(pendingRef.current.filter(x => x.key !== p.key))}
              className="text-gray-400 hover:text-red-600 text-sm px-1">✕</button>
          </div>
        ))}
        {documents.map(d => (
          <div key={d._id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
            <button type="button" onClick={() => view(d)} className="flex-1 min-w-[10rem] text-left truncate font-medium text-indigo-700 hover:underline" title="Open">
              {d.fileName}
            </button>
            <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600">{d.category}</span>
            <span className="text-xs text-gray-400 w-16 text-right">{fmtSize(d.fileSize)}</span>
            <span className="text-xs text-gray-500 w-56 truncate" title={`${d.uploadedByName || ''} ${fmtDate(d.uploadedAt)}`}>
              {d.uploadedByName || '—'} · {fmtDate(d.uploadedAt)}
            </span>
            <button type="button" onClick={() => download(d)} className="text-xs text-gray-500 hover:text-indigo-700" title="Download">⬇</button>
            {canEdit && (
              <button type="button" onClick={() => remove(d)} className="text-gray-400 hover:text-red-600 text-sm px-1" title="Remove">✕</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

ProcurementDocuments.displayName = 'ProcurementDocuments';

export default ProcurementDocuments;
